export interface QuickNavigationResult {
  destination: string;
  response: string;
}

export type ContactTypeFilter = 'customer' | 'supplier' | 'both';

export const resolveContactTypeFilter = (text: string): ContactTypeFilter | null => {
  const lower = text.toLowerCase();
  const hasCustomer = /\b(?:customers?|clients?|buyers?)\b/.test(lower);
  const hasSupplier = /\b(?:suppliers?|vendors?|sellers?)\b/.test(lower);
  if (hasCustomer && hasSupplier) return 'both';
  if (hasSupplier) return 'supplier';
  if (hasCustomer) return 'customer';
  return null;
};

export const buildContactsRoute = (text: string): string => {
  const type = resolveContactTypeFilter(text);
  return type ? `/contacts?type=${type}` : '/contacts';
};

interface QuickNavPattern {
  pattern: RegExp;
  destination: string;
  response: string;
}

const NAV_ACTION_PATTERN =
  /(?:add|create|make|generate|new|record|send|receive|stock)\s+.*?(?:units?\s+)?(?:to\s+)?(?:inventory|stock)|(?:create|make|generate|new|add|record|send)\s+(?:a\s+)?(?:new\s+)?(?:quotation|quote|estimate|invoice|bill|expense|payment|contact|customer|supplier)|(?:add|create|receive|stock)\s+(?:.*?\s+)?(?:to\s+)?(?:inventory|stock)/i;

export const quickNavPatterns: QuickNavPattern[] = [
  { pattern: /^(show|view|display)\s+(the\s+)?(dashboard|home)$/i, destination: '/dashboard', response: 'Dashboard' },
  { pattern: /^(show|view|display)\s+(the\s+)?(contacts|customers|customer|suppliers|supplier|vendors|vendor)$/i, destination: '/contacts', response: 'Contacts' },
  { pattern: /^(show|view|display)\s+(the\s+)?(invoices|invoice|bills|bill)$/i, destination: '/invoices', response: 'Invoices' },
  { pattern: /^(show|view|display)\s+(the\s+)?(reports|report|analytics)$/i, destination: '/reports', response: 'Reports' },
  { pattern: /^(show|view|display)\s+(the\s+)?(quotes|quote|quotations|quotation|estimates|estimate)$/i, destination: '/quotes', response: 'Quotes' },
  { pattern: /^(dashboard|home)$/i, destination: '/dashboard', response: 'Dashboard' },
  { pattern: /^(contacts|customers|customer|suppliers|supplier|vendors|vendor)$/i, destination: '/contacts', response: 'Contacts' },
  { pattern: /^(invoices|invoice|bills|bill)$/i, destination: '/invoices', response: 'Invoices' },
  { pattern: /^(quotes|quote|quotations|quotation)$/i, destination: '/quotes', response: 'Quotes' },
  { pattern: /^(expenses|expense|costs|cost)$/i, destination: '/expenses', response: 'Expenses' },
  { pattern: /^(reports|report|analytics)$/i, destination: '/reports', response: 'Reports' },
  { pattern: /^(settings|setting|config)$/i, destination: '/settings', response: 'Settings' },
  { pattern: /^(bank|bank accounts|accounts|account)$/i, destination: '/bank-accounts', response: 'Bank Accounts' },
  { pattern: /^(inventory|stock)$/i, destination: '/inventory', response: 'Inventory' },
  { pattern: /^(chart of accounts|chart|coa)$/i, destination: '/chart-of-accounts', response: 'Chart of Accounts' },
  { pattern: /^(image processing|image|processing|scan|document|ocr)$/i, destination: '/image-processing', response: 'Image Processing' },
  { pattern: /^(bulk payments|bulk|payments)$/i, destination: '/bulk-payments', response: 'Bulk Payments' },
  { pattern: /^(account transfers|transfers|transfer)$/i, destination: '/account-transfers', response: 'Account Transfers' },
  { pattern: /^(journal entries|journal|entries)$/i, destination: '/journal-entries', response: 'Journal Entries' },
  { pattern: /^(tax periods|tax|periods)$/i, destination: '/tax-periods', response: 'Tax Periods' },
  { pattern: /^(business management|my businesses)$/i, destination: '/business-management', response: 'Business Management' },
  { pattern: /^(super admin|super admin panel)$/i, destination: '/super-admin', response: 'Super Admin' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(dashboard|home)$/i, destination: '/dashboard', response: 'Dashboard' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(contacts|customers|customer|suppliers|supplier|vendors|vendor)$/i, destination: '/contacts', response: 'Contacts' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(invoices|invoice|bills|bill)$/i, destination: '/invoices', response: 'Invoices' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(expenses|expense|costs|cost)$/i, destination: '/expenses', response: 'Expenses' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(reports|report|analytics)$/i, destination: '/reports', response: 'Reports' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(settings|setting|config)$/i, destination: '/settings', response: 'Settings' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(bank|bank accounts|accounts|account)$/i, destination: '/bank-accounts', response: 'Bank Accounts' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(inventory|stock)$/i, destination: '/inventory', response: 'Inventory' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(chart of accounts|chart|coa)$/i, destination: '/chart-of-accounts', response: 'Chart of Accounts' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(image processing|image|processing|scan|document|ocr)$/i, destination: '/image-processing', response: 'Image Processing' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(bulk payments|bulk|payments)$/i, destination: '/bulk-payments', response: 'Bulk Payments' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(account transfers|transfers|transfer)$/i, destination: '/account-transfers', response: 'Account Transfers' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(quotes|quote|quotations|quotation|estimates|estimate)$/i, destination: '/quotes', response: 'Quotes' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(journal entries|journal|entries)$/i, destination: '/journal-entries', response: 'Journal Entries' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(tax periods|tax|periods)$/i, destination: '/tax-periods', response: 'Tax Periods' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(business management|my businesses)$/i, destination: '/business-management', response: 'Business Management' },
  { pattern: /^(go to|navigate to|show me|open|take me to)\s+(super admin|super admin panel)$/i, destination: '/super-admin', response: 'Super Admin' },
  { pattern: /^(the\s+)?(dashboard|home)$/i, destination: '/dashboard', response: 'Dashboard' },
  { pattern: /^(the\s+)?(contacts|customers|customer|suppliers|supplier|vendors|vendor)$/i, destination: '/contacts', response: 'Contacts' },
  { pattern: /^(the\s+)?(invoices|invoice|bills|bill)$/i, destination: '/invoices', response: 'Invoices' },
  { pattern: /^(the\s+)?(expenses|expense|costs|cost)$/i, destination: '/expenses', response: 'Expenses' },
  { pattern: /^(the\s+)?(reports|report|analytics)$/i, destination: '/reports', response: 'Reports' },
  { pattern: /^(the\s+)?(settings|setting|config)$/i, destination: '/settings', response: 'Settings' },
  { pattern: /^(the\s+)?(bank|bank accounts|accounts|account)$/i, destination: '/bank-accounts', response: 'Bank Accounts' },
  { pattern: /^(the\s+)?(inventory|stock)$/i, destination: '/inventory', response: 'Inventory' }
];

export const isNavigationActionCommand = (text: string): boolean => NAV_ACTION_PATTERN.test(text.toLowerCase());

export const checkQuickNavigation = (text: string): QuickNavigationResult | null => {
  const normalizedText = text.toLowerCase().trim();
  if (isNavigationActionCommand(normalizedText)) return null;
  const matched = quickNavPatterns.find(({ pattern }) => pattern.test(normalizedText));
  if (!matched) return null;

  let destination = matched.destination;
  if (destination === '/contacts') {
    destination = buildContactsRoute(normalizedText);
  }

  return { destination, response: matched.response };
};

export const executeNavigation = (destination: string): void => {
  if (window.history && window.history.pushState) {
    window.history.pushState(null, '', destination);
    window.dispatchEvent(new PopStateEvent('popstate'));
  } else {
    window.location.href = destination;
  }
};

/** Assistant follow-up prompts must not trigger background page navigation. */
export const isAssistantClarificationMessage = (message = ''): boolean => {
  const lower = message.toLowerCase().trim();
  if (!lower) return false;
  if (/\?/.test(lower)) return true;
  return /\b(?:should i|do you mean|who is this|please provide|what is the|which one|i don't have a|i do not have a|say the full name|please say)\b/.test(lower);
};

export const getDestinationFromMessage = (message = ''): string | null => {
  const lower = message.toLowerCase().trim();
  if (!lower) return null;

  if (isAssistantClarificationMessage(lower)) return null;

  // Clarification prompts (e.g. "invoice_date?") are not navigation commands
  if (/\b(?:invoice_date|due_date|payment_date|issue_date|expiry_date|customer_name|supplier_name|contact_name|item_description)\b/.test(lower)) {
    return null;
  }

  if (/\bwhich quote\b/i.test(lower) || /\bconvert\b.*\bquote\b/i.test(lower)) {
    return null;
  }

  if (/\b(?:dashboard|home)\b/.test(lower)) return '/dashboard';
  if (/\bbusiness management\b/.test(lower)) return '/business-management';
  if (/\bsuper admin\b/.test(lower)) return '/super-admin';
  if (/\b(?:contacts?|customers?|suppliers?|vendors?|clients?)\b/.test(lower)) return buildContactsRoute(lower);
  if (/\b(?:quotations?|quotes?|estimates?)\b/.test(lower)) return '/quotes';
  if (/\b(?:payables?|accounts\s+payable|purchase\s+invoices?|outstanding\s+bills?)\b/.test(lower)) {
    return '/invoices?tab=ap';
  }
  if (/\b(?:receivables?|accounts\s+receivable|sales\s+invoices?)\b/.test(lower)) {
    return '/invoices?tab=ar';
  }
  if (/\b(?:invoices?|bills?)\b/.test(lower)) return '/invoices';
  if (/\b(?:expenses?|costs?)\b/.test(lower)) return '/expenses';
  if (/\breports?\b/.test(lower)) return '/reports';
  if (/\bsettings?\b/.test(lower)) return '/settings';
  if (/\bbank\b/.test(lower)) return '/bank-accounts';
  if (/\binventory\b/.test(lower)) return '/inventory';
  if (/\b(?:image processing|scan|document|ocr)\b/.test(lower)) return '/image-processing';
  if (/\bbulk payments\b/.test(lower) || /\bbulk\b/.test(lower)) return '/bulk-payments';
  if (/\b(?:account transfers|transfers?)\b/.test(lower)) return '/account-transfers';
  if (/\b(?:journal entries|journal)\b/.test(lower)) return '/journal-entries';
  if (/\b(?:tax periods|tax)\b/.test(lower)) return '/tax-periods';
  if (/\bchart of accounts\b/.test(lower) || /\bchart\b/.test(lower)) return '/chart-of-accounts';
  return null;
};

export const routeFromDestination = (destination = ''): string => {
  const routeMap: Record<string, string> = {
    dashboard: '/dashboard',
    contacts: '/contacts',
    invoices: '/invoices',
    quotes: '/quotes',
    expenses: '/expenses',
    reports: '/reports',
    settings: '/settings',
    inventory: '/inventory',
    'bank accounts': '/bank-accounts',
    'journal entries': '/journal-entries',
    'bulk payments': '/bulk-payments',
    'account transfers': '/account-transfers',
    'chart of accounts': '/chart-of-accounts',
    'tax periods': '/tax-periods',
    'image processing': '/image-processing',
    'business management': '/business-management',
    'super admin': '/super-admin',
    payables: '/invoices?tab=ap',
    'accounts payable': '/invoices?tab=ap',
    bills: '/invoices?tab=ap',
    receivables: '/invoices?tab=ar',
    'accounts receivable': '/invoices?tab=ar'
  };
  const key = destination.toLowerCase().trim().replace(/^the\s+/, '');
  return routeMap[key] || routeMap[`${key}s`] || '/dashboard';
};
